// How a company gives buyers its return terms (Delivery → Settings): a link to
// its own page, its own text (written in a dialog, shown on its seller page), or
// Aldoo's standard return policy (the Terms of Use, 10.4) — which a company
// without its own accepts by its payout contract. The choice is saved once it
// has what it needs (a link, a text of RETURN_POLICY_MIN_TEXT characters or more);
// until then it is only picked here, and the one saved before stays in force.
import React, { useEffect, useState } from "react"
import { twMerge } from "tailwind-merge"
import { LinkIcon, DocumentTextIcon, ShieldCheckIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import { Field, TextInput, TextArea } from "../AdminShared/ui"
import usePortalTexts from "../Vendor/texts/usePortalTexts"
import { COMPANY } from "../Vendor/texts/bundles"
import { RETURN_POLICY_MIN_TEXT, RETURN_POLICY_URL } from "../Vendor/EligibilityChecklist"
import useSiteLanguage from "../Storefront/hooks/useSiteLanguage"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"
import { legalHtml, legalSection } from "../Storefront/pages/LegalPage"

export const RETURN_POLICY_MAX_TEXT = 10000

const MODES = [
  { value: "link", icon: LinkIcon, label: "Link to my policy", hint: "A page on your own site with your return terms" },
  { value: "text", icon: DocumentTextIcon, label: "My own text", hint: "Written here and shown on your seller page" },
  { value: "aldoo", icon: ShieldCheckIcon, label: "Aldoo's standard return policy", hint: "The rules in Aldoo's Terms of Use, in line with the law" },
]

// what Aldoo's standard return policy says, in a line each (the Terms, 10.4)
const ALDOO_POINTS = [
  "14 days to withdraw from delivery, without a reason (or your longer return period)",
  "The buyer pays the return shipping, unless the item is faulty or not as described",
  "The buyer is refunded within 14 days of withdrawing",
  "Digital content is not returnable once supplied; nor are personalised items or unsealed hygiene items",
]

/** The policy's rules as { key: message } for the fields that fail (useVendorForm `validate`). */
export function returnPolicyErrors(values, t) {
  const errors = {}
  const url = String(values.returnPolicyUrl ?? "").trim()
  if ((url && !RETURN_POLICY_URL.test(url)) || (values.returnPolicyMode === "link" && !url)) errors.returnPolicyUrl = t`Enter a full link to your return policy, starting with https://`
  if (values.returnPolicyMode === "text" && String(values.returnPolicyText ?? "").trim().length < RETURN_POLICY_MIN_TEXT) errors.returnPolicyText = t("Write your return policy: at least %s characters", RETURN_POLICY_MIN_TEXT)
  return errors
}

// a legal text in the portal's admin look (the storefront's LegalPage, smaller)
const PROSE =
  "text-sm leading-relaxed text-admin_text [&_p]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-2 [&_li]:my-1 " +
  "[&_a]:text-admin_primary [&_a]:underline [&_strong]:font-semibold"

/** Aldoo's standard return policy as the Terms of Use say it (10.4), in the site's language. */
function AldooPolicyDialog({ onClose }) {
  const t = usePortalTexts(COMPANY)
  const language = useSiteLanguage() === "bg" ? "bg" : "en"
  const [markdown, setMarkdown] = useState(null)
  useEffect(() => {
    let live = true
    const load = language === "bg" ? import("../Storefront/legal/bg/obshti-usloviya.md?raw") : import("../Storefront/legal/en/terms-of-use.md?raw")
    load.then((module) => live && setMarkdown(legalSection(module.default, "standard-return-policy"))).catch(() => live && setMarkdown(""))
    return () => {
      live = false
    }
  }, [language])
  const prefix = language === "bg" ? "/bg" : "/en"
  return (
    <Modal
      isOpen
      onClose={onClose}
      closeOnEscape
      variant="admin_compact"
      title={t`Aldoo's standard return policy`}
      styleOverrides={{ container: "bg-admin_paper w-full max-w-[760px] max-h-[90vh] overflow-y-auto rounded-md border border-admin_bordered shadow-lg" }}
    >
      <div className="flex flex-col gap-3 px-4 pb-2" data-testid="aldoo-return-policy-dialog">
        {markdown === null ? (
          <div className="py-8 text-center text-sm text-admin_text_secondary">{t`Loading…`}</div>
        ) : (
          <div className={PROSE} dangerouslySetInnerHTML={{ __html: legalHtml(markdown).replace(/<a href=/g, '<a target="_blank" rel="noreferrer" href=') }} />
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-admin_bordered pt-3">
          <div className="flex flex-wrap gap-4 text-sm">
            <a href={`${prefix}/terms-of-use#standard-return-policy`} target="_blank" rel="noreferrer" className="text-admin_primary underline">{t`Open in the Terms of Use`}</a>
            <a href={`${prefix}/returns-policy`} target="_blank" rel="noreferrer" className="text-admin_primary underline">{t`Read the full returns policy`}</a>
          </div>
          <Button variant="admin_primary" onClick={onClose}>{t`Close`}</Button>
        </div>
      </div>
    </Modal>
  )
}

/** The company's return policy, written or pasted: plain text, its lines kept. */
function PolicyTextDialog({ value, onSave, onClose }) {
  const t = usePortalTexts(COMPANY)
  const [draft, setDraft] = useState(value || "")
  const length = draft.trim().length
  const short = length < RETURN_POLICY_MIN_TEXT
  return (
    <Modal
      isOpen
      onClose={onClose}
      variant="admin_compact"
      title={t`Your return policy`}
      styleOverrides={{ container: "bg-admin_paper w-full max-w-[760px] max-h-[90vh] overflow-y-auto rounded-md border border-admin_bordered shadow-lg" }}
    >
      <div className="flex flex-col gap-3 px-4 pb-2" data-testid="return-policy-text-dialog">
        <p className="m-0 text-sm text-admin_text_secondary">
          {t`Write or paste your return terms: how long buyers have, who pays the return shipping, the state items must be in. Plain text; line breaks are kept. Buyers read it on your seller page. It cannot take away buyers' rights under the law.`}
        </p>
        <TextArea value={draft} onChange={setDraft} rows={16} maxLength={RETURN_POLICY_MAX_TEXT} autoFocus className="min-h-[320px] leading-relaxed" data-testid="return-policy-text-input" />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className={twMerge("text-xs", short ? "text-admin_error" : "text-admin_text_secondary")} data-testid="return-policy-text-count">
            {t("%s / %s characters", draft.length, RETURN_POLICY_MAX_TEXT)}
            {short && ` · ${t("At least %s characters", RETURN_POLICY_MIN_TEXT)}`}
          </span>
          <div className="flex gap-2">
            <Button variant="admin_secondary" onClick={onClose}>{t`Cancel`}</Button>
            <Button variant="admin_primary" disabled={short} onClick={() => onSave(draft)} data-testid="return-policy-text-save">{t`Save`}</Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

/**
 * The choice and what goes with it. `form`, `set`, `flush`, `errors`: the
 * Delivery tab's useVendorForm; returnPolicyMode is the saved choice.
 */
export default function ReturnPolicyField({ form, set, flush, errors = {}, canEdit }) {
  const t = usePortalTexts(COMPANY)
  const saved = form.returnPolicyMode || ""
  const [picked, setPicked] = useState("")
  const [writing, setWriting] = useState(false)
  const [viewing, setViewing] = useState(false)
  const mode = picked || saved
  const url = String(form.returnPolicyUrl ?? "").trim()
  const text = String(form.returnPolicyText ?? "")
  const hasText = text.trim().length >= RETURN_POLICY_MIN_TEXT

  // the picked way saved: nothing is waiting any more
  useEffect(() => {
    if (picked && picked === saved) setPicked("")
  }, [picked, saved])

  const choose = (value) => {
    if (!canEdit || value === mode) return
    const ready = value === "aldoo" || (value === "link" && RETURN_POLICY_URL.test(url)) || (value === "text" && hasText)
    if (ready) {
      setPicked("")
      return set({ returnPolicyMode: value }, { now: true })
    }
    setPicked(value)
    if (value === "text") setWriting(true)
  }

  const saveText = (draft) => {
    setWriting(false)
    setPicked("")
    set({ returnPolicyMode: "text", returnPolicyText: draft }, { now: true })
  }

  const pending = picked && picked !== saved
  const savedLabel = MODES.find((m) => m.value === saved)?.label
  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs font-medium text-admin_text_secondary inline-flex items-center gap-1.5">{t`Return policy *`}<HelpHint helpKey="vendor.delivery#return-policy" /></span>
      <div role="radiogroup" aria-label={t`Return policy`} className="grid gap-2 md:grid-cols-3" data-testid="return-policy" data-mode={mode}>
        {MODES.map(({ value, icon: Icon, label, hint }) => {
          const on = mode === value
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={on}
              disabled={!canEdit}
              onClick={() => choose(value)}
              className={twMerge(
                "flex items-start gap-3 rounded-md border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60",
                on ? "border-admin_primary bg-admin_primary/5 ring-1 ring-admin_primary" : "border-admin_bordered bg-admin_paper hover:border-admin_primary/60"
              )}
              data-testid={`return-policy-mode-${value}`}
            >
              {/* the choice's mark, drawn in the theme like the framework checkbox */}
              <span className={twMerge("mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-2", on ? "border-admin_primary" : "border-admin_neutral_2")}>
                {on && <span className="h-2 w-2 rounded-full bg-admin_primary" />}
              </span>
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="flex items-center gap-1.5 text-sm font-medium text-admin_text">
                  <Icon className="h-4 w-4 shrink-0 text-admin_text_secondary" />
                  {t(label)}
                </span>
                <span className="text-xs text-admin_text_secondary">{t(hint)}</span>
              </span>
            </button>
          )
        })}
      </div>
      {!mode && <span className="text-[11px] text-admin_error" role="alert">{t`Choose how buyers get your return terms`}</span>}
      {pending && (
        <span className="text-[11px] text-admin_warning" data-testid="return-policy-pending">
          {picked === "link" ? t`Not saved yet: add the link to use it.` : t`Not saved yet: write the text to use it.`}
          {savedLabel && ` ${t("Until then buyers get: %s.", t(savedLabel))}`}
        </span>
      )}

      {mode === "link" && (
        <Field label={t`Return policy link`} hint={t`A page on your site with your return terms, starting with https://`} error={errors.returnPolicyUrl || (!url ? t`Required` : null)}>
          <TextInput
            value={form.returnPolicyUrl ?? ""}
            onChange={(v) => set({ returnPolicyUrl: v, ...(RETURN_POLICY_URL.test(v.trim()) ? { returnPolicyMode: "link" } : {}) })}
            onBlur={flush}
            disabled={!canEdit}
            placeholder="https://"
            data-testid="return-policy-url"
          />
        </Field>
      )}

      {mode === "text" && (
        <div className="flex flex-col gap-2 rounded-md border border-admin_bordered bg-admin_neutral_1/40 p-3" data-testid="return-policy-text">
          {text.trim() ? (
            <div className="line-clamp-4 whitespace-pre-line break-words text-sm text-admin_text" data-testid="return-policy-text-summary">{text}</div>
          ) : (
            <div className="text-sm italic text-admin_text_secondary">{t`No text yet. Write or paste your return policy; buyers read it on your seller page.`}</div>
          )}
          {errors.returnPolicyText && <span className="text-[11px] text-admin_error" role="alert">{errors.returnPolicyText}</span>}
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="admin_secondary" onClick={() => setWriting(true)} disabled={!canEdit} className="h-auto min-h-[38px] max-w-full whitespace-normal py-1.5 text-center" data-testid="return-policy-text-edit">
              {text.trim() ? t`Edit the text` : t`Write / paste the text`}
            </Button>
            {text.trim() && <span className="text-xs text-admin_text_secondary">{t("%s characters", text.length)}</span>}
          </div>
        </div>
      )}

      {mode === "aldoo" && (
        <div className="flex flex-col gap-2 rounded-md border border-admin_bordered bg-admin_neutral_1/40 p-3" data-testid="return-policy-aldoo">
          <span className="text-sm text-admin_text">{t`Buyers get Aldoo's standard return policy, which you accept in your payout contract:`}</span>
          <ul className="m-0 list-disc pl-5 text-sm text-admin_text_secondary">
            {ALDOO_POINTS.map((point) => (
              <li key={point}>{t(point)}</li>
            ))}
          </ul>
          <div>
            <Button variant="admin_secondary" onClick={() => setViewing(true)} className="h-auto min-h-[38px] max-w-full whitespace-normal py-1.5 text-center" data-testid="return-policy-aldoo-view">{t`View Aldoo's return policy`}</Button>
          </div>
        </div>
      )}

      {writing && <PolicyTextDialog value={text} onSave={saveText} onClose={() => setWriting(false)} />}
      {viewing && <AldooPolicyDialog onClose={() => setViewing(false)} />}
    </div>
  )
}
